import type { UseFormReturn } from 'react-hook-form';
import type { DynamicPricingMode, PricingStrategyProfileResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';
import { StrategyProfileExplorer } from './StrategyProfileExplorer';

interface Props {
  form: UseFormReturn<PricingConfigurationFormValues>;
  profiles: PricingStrategyProfileResponse[];
  activeProfileVersionId: string | null;
  activeProfile?: PricingStrategyProfileResponse;
  activeProfileProblem?: string | null;
  onRetryActiveProfile?: () => void;
  publicationControlsAvailable: boolean;
  errorFor: (field: 'mode' | 'strategyProfileVersionId') => string | undefined;
}

const MODES: { mode: DynamicPricingMode; label: string; hint: string }[] = [
  {
    mode: 'Off',
    label: 'Aus',
    hint: 'Keine automatischen Werte. Wird ohne Vorschau sofort gespeichert.',
  },
  {
    mode: 'MinimumStayOnly',
    label: 'Nur Mindestaufenthalt',
    hint: 'Die Automatik setzt nur Mindestnächte. Preise bleiben deine.',
  },
  {
    mode: 'Automatic',
    label: 'Vollautomatisch',
    hint: 'Preise und Mindestnächte kommen von der Automatik.',
  },
];

export const MODE_LABEL: Record<DynamicPricingMode, string> = {
  Off: 'Aus',
  MinimumStayOnly: 'Nur Mindestaufenthalt',
  Automatic: 'Vollautomatisch',
};

/** Abschnitt 1: der eine Regler für den Modus und die Wahl des Strategieprofils. */
export function StatusStrategySection(props: Props) {
  const { form } = props;
  const modeError = props.errorFor('mode');
  return (
    <>
      <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        Automationsmodus
      </span>
      <div
        role="radiogroup"
        aria-label="Automationsmodus"
        className="grid grid-cols-[repeat(auto-fit,minmax(min(180px,100%),1fr))] gap-2.5"
      >
        {MODES.map(({ mode, label, hint }) => (
          <label
            key={mode}
            className={cn(
              'border-hairline bg-card flex min-h-[92px] cursor-pointer flex-col gap-1.5 rounded-lg border p-3.5',
              'has-[:checked]:border-forest has-[:checked]:bg-forest-soft/50 has-[:checked]:border-2',
              'has-[:focus-visible]:outline-forest has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
              'has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50',
            )}
          >
            <span className="flex items-center gap-2">
              <input
                type="radio"
                value={mode}
                aria-labelledby={`mode-${mode}`}
                aria-describedby={`mode-${mode}-hint`}
                // Ohne Publikationssteuerung kann nichts rausgehen – dann bleibt nur „Aus“.
                disabled={mode !== 'Off' && !props.publicationControlsAvailable}
                className="accent-forest size-4"
                {...form.register('mode')}
              />
              <span id={`mode-${mode}`} className="text-[15px] font-bold">
                {label}
              </span>
            </span>
            <span id={`mode-${mode}-hint`} className="text-ink-soft text-xs leading-[1.45]">
              {hint}
            </span>
          </label>
        ))}
      </div>
      {!props.publicationControlsAvailable && (
        <p className="bg-amber-tint text-amber-ink rounded-control px-3 py-2 text-sm">
          Die Publikationssteuerung ist auf dem Server noch nicht verfügbar. Deshalb lässt sich nur
          „Aus“ wählen.
        </p>
      )}
      {modeError && <p className="text-sealwax text-sm">{modeError}</p>}
      <input type="hidden" {...form.register('strategyProfileVersionId')} />
      <StrategyProfileExplorer
        profiles={props.profiles}
        selectedProfileVersionId={form.watch('strategyProfileVersionId') || null}
        activeProfileVersionId={props.activeProfileVersionId}
        activeProfile={props.activeProfile}
        activeProfileProblem={props.activeProfileProblem}
        onRetryActiveProfile={props.onRetryActiveProfile}
        error={props.errorFor('strategyProfileVersionId')}
        onSelect={(versionId) =>
          form.setValue('strategyProfileVersionId', versionId, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
    </>
  );
}
